import { get } from '@/lib/api';
import { clearAuthSession, getAuthToken, setAuthSession, setStoredUser } from '@/lib/auth';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Wraps protected routes. Checks for a stored JWT or shared httpOnly cookie,
 * validates against GET /auth/me, and redirects to /login if invalid.
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>(
    'checking',
  );

  useEffect(() => {
    // Always call /auth/me — it will use the Bearer token from localStorage
    // or the shared httpOnly cookie (via credentials: 'include') automatically.
    get<{ id: string; email: string; accessToken?: string }>('/auth/me')
      .then((user) => {
        // If we got back a fresh accessToken (e.g. from cookie-only session),
        // store it in localStorage for future Bearer-header requests.
        if (user.accessToken && !getAuthToken()) {
          setAuthSession(user.accessToken, user);
        } else {
          setStoredUser(user);
        }
        setStatus('authenticated');
      })
      .catch(() => {
        // No valid session — clear and redirect
        clearAuthSession();
        setStatus('unauthenticated');
        navigate('/login', { replace: true });
      });
  }, [navigate]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#1e1e1e] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#858585] animate-spin" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return null;
  }

  return <>{children}</>;
}
